import React from "react";
import { Paperclip, Upload, FileText, Trash2 } from "lucide-react";
import { AppModal, AppModalHeader } from "../../../components/AppModal";
import { APP_BTN_OUTLINE_CLASS } from "../../../ui/controlStyles";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import type { OfferAttachment } from "../../../types";
import {
  deleteOfferAttachmentStorage,
  uploadOfferAttachment,
} from "../utils/offerAttachmentsApi";
import { legalAttachmentLabel, type LegalAttachmentKind } from "../utils/offerLegalAttachments";
import { OfferAttachmentDocumentPicker } from "./OfferAttachmentDocumentPicker";
import { SortableOfferImageTile } from "./SortableOfferImageTile";

export type OfferStandardAttachmentRow = {
  kind: LegalAttachmentKind;
  filename: string;
  excluded: boolean;
};

type OfferAttachmentsButtonProps = {
  leadRouteId: string;
  attachments: OfferAttachment[];
  onChange: (next: OfferAttachment[]) => void | Promise<void>;
  className?: string;
  label?: React.ReactNode;
  standardAttachments?: OfferStandardAttachmentRow[];
  onExcludeStandard?: (kind: LegalAttachmentKind) => void | Promise<void>;
  onRestoreStandard?: (kind: LegalAttachmentKind) => void | Promise<void>;
};

const ACCEPT = "application/pdf,image/png,image/jpeg";

export const OfferAttachmentsButton: React.FC<OfferAttachmentsButtonProps> = ({
  leadRouteId,
  attachments,
  onChange,
  className,
  label,
  standardAttachments = [],
  onExcludeStandard,
  onRestoreStandard,
}) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const existingObjectPaths = React.useMemo(
    () => new Set(attachments.map((a) => a.object_path)),
    [attachments],
  );

  const addDocuments = async (added: OfferAttachment[]) => {
    const fresh = added.filter((a) => !existingObjectPaths.has(a.object_path));
    if (fresh.length === 0) return;
    await onChange([...attachments, ...fresh]);
  };
  const sortSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = React.useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = attachments.findIndex((a) => a.id === active.id);
      const newIndex = attachments.findIndex((a) => a.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      await onChange(arrayMove(attachments, oldIndex, newIndex));
    },
    [attachments, onChange],
  );

  const close = () => {
    if (busy) return;
    setOpen(false);
    setError(null);
  };

  const uploadFiles = async (files: FileList | File[]) => {
    setBusy(true);
    setError(null);
    try {
      const next: OfferAttachment[] = [...attachments];
      for (const file of Array.from(files)) {
        next.push(await uploadOfferAttachment(leadRouteId, file));
      }
      await onChange(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload fehlgeschlagen.");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = async (attachment: OfferAttachment) => {
    setBusy(true);
    setError(null);
    try {
      await deleteOfferAttachmentStorage(leadRouteId, attachment);
      await onChange(attachments.filter((a) => a.id !== attachment.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Löschen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const count = attachments.length + standardAttachments.filter((s) => !s.excluded).length;

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => {
          setOpen(true);
          setError(null);
        }}
      >
        {label ?? (
          <>
            <Paperclip className="w-3.5 h-3.5 shrink-0 text-pipedrive-blue" />
            Anhänge{count > 0 ? ` (${count})` : ""}
          </>
        )}
      </button>

      <AppModal
        open={open}
        onClose={close}
        disableClose={busy}
        overlayClassName="z-[260]"
        panelClassName="md:max-w-2xl"
        ariaLabelledBy="offer-attachments-title"
      >
        <AppModalHeader titleId="offer-attachments-title" title="Anhänge" onClose={close} closeDisabled={busy} />

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <p className="text-xs text-app-text-subtle">
            Diese Dateien werden hinter das eigentliche PDF angehängt. Ziehe die Kacheln, um Bilder
            und PDFs gemischt zu sortieren. Bilder im Dokumenttext fügst du über „Bilder hinzufügen“
            ein.
          </p>

          {attachments.length > 0 ? (
            <DndContext
              sensors={sortSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => void handleDragEnd(event)}
            >
              <SortableContext
                items={attachments.map((a) => a.id)}
                strategy={rectSortingStrategy}
              >
                <ul className="grid grid-cols-3 gap-3">
                  {attachments.map((a) => (
                    <SortableOfferImageTile
                      key={a.id}
                      leadRouteId={leadRouteId}
                      attachment={a}
                      busy={busy}
                      onRemove={(item) => void removeAttachment(item)}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          ) : null}

          {attachments.length === 0 && standardAttachments.length === 0 ? (
            <p className="text-sm text-app-text-subtle italic">Noch keine Anhänge.</p>
          ) : null}

          {standardAttachments.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-app-text-subtle">Aus Einstellungen</p>
              <ul className="space-y-2">
                {standardAttachments.map((item) => (
                  <li
                    key={item.kind}
                    className="flex items-center gap-3 rounded-lg border border-app-border bg-app-surface px-3 py-2"
                  >
                    <div className="w-8 h-8 rounded-md bg-app-surface-muted text-app-text-muted inline-flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-app-text truncate">{item.filename}</p>
                      <p className="text-xs text-app-text-subtle">
                        {legalAttachmentLabel(item.kind)}
                        {item.excluded ? " · entfernt" : ""}
                      </p>
                    </div>
                    {item.excluded ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void onRestoreStandard?.(item.kind)}
                        className="shrink-0 text-xs font-medium text-pipedrive-blue hover:underline disabled:opacity-50"
                      >
                        Wieder anhängen
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void onExcludeStandard?.(item.kind)}
                        disabled={busy}
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors disabled:opacity-50"
                        aria-label={`${legalAttachmentLabel(item.kind)} entfernen`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="w-full text-left rounded-xl border border-dashed border-sky-200 bg-app-surface-muted/60 px-4 py-5 hover:bg-sky-50/50 hover:border-sky-300 transition-colors disabled:opacity-60 disabled:pointer-events-none"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600">
                <Upload className="w-5 h-5" />
              </div>
              <p className="text-sm text-app-text-muted">
                Datei hier auswählen oder{" "}
                <span className="text-sky-700 font-semibold underline underline-offset-2">Datei wählen</span>
              </p>
              <p className="text-xs text-app-text-subtle">Erlaubt: PDF, PNG, JPEG (Max. 25 MB pro Datei)</p>
            </div>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept={ACCEPT}
            onChange={(e) => {
              const fs = e.target.files;
              if (fs && fs.length > 0) void uploadFiles(fs);
            }}
          />

          <OfferAttachmentDocumentPicker
            leadRouteId={leadRouteId}
            existingObjectPaths={existingObjectPaths}
            onAdd={addDocuments}
          />

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-app-border px-5 py-3">
          <button type="button" onClick={close} disabled={busy} className={APP_BTN_OUTLINE_CLASS}>
            Fertig
          </button>
        </div>
      </AppModal>
    </>
  );
};
